import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSpinner, FaPlus, FaCheckCircle, FaTrash, FaSearch } from 'react-icons/fa';
import { jobCardService } from '../../../../services/apiService';
import { getDisplayName, getProfileDisplayPhone, getProfileDisplayEmail } from '../../../../utils/displayName';

function truncate(str, len = 40) {
  const s = (str || '').toString().trim();
  return s.length <= len ? s : s.slice(0, len) + '…';
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Resolve name: name, fullName, displayName, or firstName + lastName */
function resolveCustomerName(customer) {
  if (!customer) return '';
  const n = (customer.name || customer.fullName || customer.displayName || '').trim();
  if (n) return n;
  const first = (customer.firstName || '').trim();
  const last = (customer.lastName || '').trim();
  return [first, last].filter(Boolean).join(' ').trim() || '';
}

/** Customer display: prefer name (first/last/both); fallback to phone only if no name */
function getCustomerName(customer) {
  if (!customer) return '—';
  const name = resolveCustomerName(customer);
  if (name && !/^User\s*\d+$/i.test(name) && !name.includes('@')) return name;
  const display = getDisplayName(customer);
  if (display) return display;
  const phone = getProfileDisplayPhone(customer);
  const code = (customer.phoneCode || '').toString().trim() || '+91';
  return phone ? `${code} ${phone}` : '—';
}

/** Format address for display */
function formatAddress(addr) {
  if (!addr || typeof addr !== 'object') return '—';
  const parts = [
    addr.fullAddress,
    [addr.area, addr.city, addr.state, addr.postalCode].filter(Boolean).join(', '),
    addr.country
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

const JOB_STATUSES = ['Created', 'Intake', 'PendingCustomerApproval', 'CustomerApproved', 'CustomerRejected', 'PartsOrdered', 'InProgress', 'ReadyForQC', 'QCRejected', 'QCApproved', 'Billed', 'Paid', 'Delivered', 'Closed', 'Cancelled'];

const Jobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [customerModal, setCustomerModal] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, job: null });

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const data = await jobCardService.getGarage();
      const list = Array.isArray(data) ? data : (data?.jobCards ?? data?.jobs ?? []);
      setJobs(Array.isArray(list) ? list : []);
    } catch (e) {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const uniqueVehicles = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => {
      const reg = (j.vehicle?.registrationNumber || '').toString().trim();
      const makeModel = [j.vehicle?.make, j.vehicle?.model].filter(Boolean).join(' ');
      if (reg || makeModel) set.add(reg || makeModel || '—');
    });
    return ['all', ...Array.from(set).filter(Boolean).sort()];
  }, [jobs]);

  const uniqueLocations = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => {
      const c = j.customerId;
      if (!c) return;
      const addr = c.address || {};
      const city = (addr.city || '').toString().trim();
      const state = (addr.state || '').toString().trim();
      const loc = city || state || (addr.fullAddress || '').toString().trim().slice(0, 30);
      if (loc) set.add(loc);
    });
    return ['all', ...Array.from(set).filter(Boolean).sort()];
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const customerName = getCustomerName(job.customerId);
      const searchableCustomer = [
        resolveCustomerName(job.customerId),
        (job.customerId?.firstName || '').toString(),
        (job.customerId?.lastName || '').toString(),
        getCustomerName(job.customerId),
        (job.customerId?.phone || '').toString()
      ].filter(Boolean).join(' ').toLowerCase();
      const complaint = (job.complaintDescription || (job.reportedIssues && job.reportedIssues.join(' ')) || '').toLowerCase();
      const search = (searchTerm || '').toLowerCase().trim();
      if (search) {
        const nameMatch = searchableCustomer.includes(search) || customerName.toLowerCase().includes(search);
        const complaintMatch = complaint.includes(search);
        if (!nameMatch && !complaintMatch) return false;
      }
      if (statusFilter !== 'all' && (job.status || '') !== statusFilter) return false;
      const reg = (job.vehicle?.registrationNumber || '').toString().trim();
      const makeModel = [job.vehicle?.make, job.vehicle?.model].filter(Boolean).join(' ');
      const jobVehicle = reg || makeModel || '—';
      if (vehicleFilter !== 'all' && jobVehicle !== vehicleFilter) return false;
      const addr = job.customerId?.address || {};
      const city = (addr.city || '').toString().trim();
      const state = (addr.state || '').toString().trim();
      const loc = city || state || (addr.fullAddress || '').toString().trim().slice(0, 30);
      if (locationFilter !== 'all' && loc !== locationFilter) return false;
      return true;
    });
  }, [jobs, searchTerm, statusFilter, vehicleFilter, locationFilter]);

  const handleDelete = (job) => {
    setDeleteModal({ open: true, job });
  };

  const confirmDelete = async () => {
    if (!deleteModal.job) return;
    const id = deleteModal.job._id || deleteModal.job.id;
    try {
      setDeletingId(id);
      await jobCardService.delete(id);
      setDeleteModal({ open: false, job: null });
      await fetchJobs();
    } catch (e) {
      alert(e.message || 'Failed to delete job');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Active Jobs</h2>
        <button
          onClick={() => navigate('/garage/dashboard/jobs/new')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700"
        >
          <FaPlus /> New Job Card
        </button>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search by customer name or complaint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="all">All status</option>
            {JOB_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="all">All vehicles</option>
            {uniqueVehicles.filter((v) => v !== 'all').map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="all">All locations</option>
            {uniqueLocations.filter((l) => l !== 'all').map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
        <p className="text-xs text-gray-500 mt-2">Showing {filteredJobs.length} of {jobs.length} jobs</p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="py-10 text-center text-gray-500 flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Complaint / Issues</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Manage</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map((job) => (
                  <tr key={job._id || job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">#{(job.jobCardId || job._id || '').toString().slice(-8)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setCustomerModal(job.customerId)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                      >
                        {getCustomerName(job.customerId)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className="block">{(job.vehicle?.registrationNumber || '—').toString()}</span>
                      <span className="text-gray-500 text-xs">{[job.vehicle?.make, job.vehicle?.model, job.vehicle?.variant].filter(Boolean).join(' ') || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px]" title={job.complaintDescription || (job.reportedIssues && job.reportedIssues.join('; '))}>
                      {truncate(job.complaintDescription || (job.reportedIssues && job.reportedIssues.join('; ')) || '—', 35)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(job.dropoffAt || job.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/garage/dashboard/jobs/${job._id || job.id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="View job"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => navigate(`/garage/dashboard/jobs/${job._id || job.id}`)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                          title="Approve / Continue"
                        >
                          <FaCheckCircle />
                        </button>
                        <button
                          onClick={() => handleDelete(job)}
                          disabled={deletingId === (job._id || job.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                          title="Delete job"
                        >
                          {deletingId === (job._id || job.id) ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      {jobs.length === 0 ? 'No jobs yet.' : 'No jobs match your search or filters.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer details modal */}
      {customerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCustomerModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Customer details</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Name</p>
                <p className="font-medium text-gray-900">{getCustomerName(customerModal)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Mobile</p>
                <p className="text-gray-700">{(customerModal.phoneCode || '+91')} {customerModal.phone || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Email</p>
                <p className="text-gray-700">{getProfileDisplayEmail(customerModal) || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Address</p>
                <p className="text-gray-700">{formatAddress(customerModal.address)}</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setCustomerModal(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteModal.open && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteModal({ open: false, job: null })}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete job card</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete this job card (only allowed before it is accepted / moved forward).
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteModal({ open: false, job: null })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deletingId === (deleteModal.job?._id || deleteModal.job?.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {deletingId === (deleteModal.job?._id || deleteModal.job?.id) ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
